(() => {
const typekitId = 'axis-mundi-typekit';

function loadAxisMundiTypekit() {
  if (document.getElementById(typekitId)) return;

  const stylesheet = document.createElement('link');
  stylesheet.id = typekitId;
  stylesheet.rel = 'stylesheet';
  stylesheet.href = 'https://use.typekit.net/qdb8bjf.css';
  document.head.appendChild(stylesheet);
}

class AxisMundiWorld {
  constructor(section) {
    this.section = section;
    this.sectionId = section.dataset.sectionId;
    this.isCustomerAuthenticated = section.dataset.customerAuthenticated === 'true';
    this.customerEmail = section.dataset.customerEmail || '';
    this.isServerTrialOneComplete = section.dataset.serverTrialOneComplete === 'true';
    this.isServerTrialTwoComplete = section.dataset.serverTrialTwoComplete === 'true';
    this.didCompleteTrialTwoThisSession = false;
    this.progressEndpoint = section.dataset.axisProgressEndpoint || '';
    this.isGateBypassed = section.dataset.accountGateBypassed === 'true';
    this.canEnterWorld = this.isCustomerAuthenticated || this.isGateBypassed;
    this.storageKey = `axis-mundi-world:${this.sectionId}`;
    this.legacyEntryKey = `album-landing-entered:${this.sectionId}`;
    this.authDestinationKey = `axis-mundi-auth-destination:${this.sectionId}`;
    this.accountLoginUrl = section.dataset.accountLoginUrl;
    this.views = Array.from(section.querySelectorAll('[data-axis-view]'));
    this.modal = section.querySelector('[data-axis-gate-modal]');
    this.introTransition = section.querySelector('[data-axis-intro-transition]');
    this.introLine = section.querySelector('[data-axis-intro-line]');
    this.featuredTrial = section.querySelector('[data-axis-featured-trial]');
    this.featuredTrialNumeral = section.querySelector('[data-axis-featured-numeral]');
    this.featuredTrialTitle = section.querySelector('[data-axis-featured-title]');
    this.featuredTrialStatus = section.querySelector('[data-axis-featured-status]');
    this.trialOptions = Array.from(section.querySelectorAll('[data-axis-select-trial]'));
    this.trials = new Map(this.trialOptions.map((option) => [
      option.dataset.axisSelectTrial,
      {
        id: option.dataset.axisSelectTrial,
        numeral: option.dataset.axisTrialNumeral || '',
        title: option.dataset.axisTrialTitle || '',
        artwork: option.dataset.axisTrialArtwork || '',
        view: option.dataset.axisTrialView || '',
        rewardsReference: option.dataset.axisTrialRewardsReference || '',
        selectable: option.dataset.axisTrialSelectable === 'true',
        option,
      },
    ]));
    this.selectedTrialId = null;
    this.chapterTwoFinale = section.querySelector('[data-axis-hollow-finale]');
    this.vaultDestination = section.querySelector('[data-axis-vault-destination]');
    this.vaultDestinationLabel = section.querySelector('[data-axis-vault-destination-label]');
    this.vaultDestinationStatus = section.querySelector('[data-axis-vault-destination-status]');
    this.vaultTrials = Array.from(section.querySelectorAll('[data-axis-vault-collection-trial]')).map((trial) => ({
      id: trial.dataset.axisVaultCollectionTrial,
      order: Number(trial.dataset.axisVaultTrialOrder) || 0,
      element: trial,
      selector: section.querySelector(`[data-axis-vault-select-trial="${trial.dataset.axisVaultCollectionTrial}"]`),
      status: trial.querySelector('[data-axis-vault-trial-status]'),
      locked: trial.querySelector('[data-axis-vault-trial-locked]'),
      collection: trial.querySelector('[data-axis-vault-trial-unlocked]'),
      video: trial.querySelector('[data-axis-vault-video]'),
      videoSource: trial.querySelector('[data-axis-vault-video-source]'),
      videoTitle: trial.querySelector('[data-axis-vault-video-title]'),
      videoOptions: Array.from(trial.querySelectorAll('[data-axis-vault-video-option]')),
    }));
    this.vaultRoadmapItems = Array.from(section.querySelectorAll('[data-axis-vault-roadmap-trial]'));
    this.vaultVideoOptions = Array.from(section.querySelectorAll('[data-axis-vault-video-option]'));
    this.prologueModal = section.querySelector('[data-axis-prologue-modal]');
    this.prologueAudio = section.querySelector('[data-axis-prologue-audio]');
    this.prologueSource = this.prologueAudio?.querySelector('source');
    this.prologueCopy = section.querySelector('[data-axis-prologue-copy]');
    this.prologueLines = Array.from(section.querySelectorAll('[data-axis-prologue-line]'));
    this.prologueTrigger = null;
    this.prologueCompletionCallback = null;
    this.prologueNarration = 'trial-1';
    this.prologueDefaultSource = this.prologueSource?.getAttribute('src') || '';
    this.prologueDefaultType = this.prologueSource?.getAttribute('type') || '';
    this.introTransitionActive = false;
    this.state = this.readState();
    const shouldMigrateTrialOne = (
      this.isCustomerAuthenticated
      && this.customerEmail
      && this.hasCompletedTrial('trial-1')
      && !this.isServerTrialOneComplete
    );
    const shouldMigrateTrialTwo = (
      this.isCustomerAuthenticated
      && this.customerEmail
      && this.hasCompletedTrial('trial-2')
      && !this.isServerTrialTwoComplete
    );
    if (this.isServerTrialOneComplete) this.completeTrial('trial-1');
    if (this.isServerTrialTwoComplete) this.completeTrial('trial-2');
    this.restoreAuthDestination();
    this.saveState();

    this.bindEvents();
    this.readChapterOneCompletion();
    this.syncTrialState();
    this.selectTrial(this.getInitialSelectedTrialId());
    if (shouldMigrateTrialOne) this.syncTrialCompletion('trial-1', 'local_storage_migration');
    if (shouldMigrateTrialTwo) this.syncTrialCompletion('trial-2', 'local_storage_migration');

    this.showView(this.getInitialView(), false);
  }

  bindEvents() {
    this.section.querySelectorAll('[data-axis-enter-trigger]').forEach((button) => {
      button.addEventListener('click', () => {
        if (this.canEnterWorld) {
          this.enterWorld();
          return;
        }

        this.openGate();
      });
    });

    this.section.querySelectorAll('[data-axis-account-login]').forEach((link) => {
      link.addEventListener('click', () => this.rememberAuthDestination());
    });

    this.section.querySelectorAll('[data-axis-open-chapter]').forEach((button) => {
      button.addEventListener('click', () => this.showView(button.dataset.axisOpenChapter));
    });

    this.trialOptions.forEach((button) => {
      if (button.dataset.axisTrialSelectable !== 'true') return;
      button.addEventListener('click', () => this.selectTrial(button.dataset.axisSelectTrial, true));
    });

    this.section.querySelectorAll('[data-axis-open-rewards]').forEach((button) => {
      button.addEventListener('click', () => {
        if (this.getHighestCompletedVaultTrial()) this.showView('rewards');
      });
    });

    this.section.querySelectorAll('[data-axis-back-to-chapters]').forEach((button) => {
      button.addEventListener('click', () => this.showView('chapter-select'));
    });

    this.section.querySelectorAll('[data-axis-open-prologue]').forEach((button) => {
      button.addEventListener('click', () => this.openPrologue(button));
    });

    this.section.querySelectorAll('[data-axis-close-prologue]').forEach((button) => {
      button.addEventListener('click', () => this.closePrologue());
    });

    if (this.prologueAudio) {
      this.prologueAudio.addEventListener('timeupdate', () => this.syncPrologueLine());
      this.prologueAudio.addEventListener('seeked', () => this.syncPrologueLine());
      this.prologueAudio.addEventListener('ended', () => this.handlePrologueEnded());
    }

    if (this.prologueModal) {
      this.prologueModal.addEventListener('click', (event) => {
        if (event.target === this.prologueModal) this.closePrologue();
      });
    }

    this.vaultVideoOptions.forEach((button, index) => {
      button.addEventListener('click', () => this.selectVaultVideo(button));
      button.addEventListener('keydown', (event) => this.handleVaultVideoKeydown(event, button, index));
    });

    this.vaultTrials.forEach((trial) => {
      trial.selector?.addEventListener('click', () => this.selectVaultTrial(trial.id, true));
      trial.selector?.addEventListener('keydown', (event) => this.handleVaultTrialKeydown(event, trial));
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.prologueModal && !this.prologueModal.hidden) {
        this.closePrologue();
      }
    });

    this.section.addEventListener('axis:chapter-complete', (event) => {
      if (event.detail && event.detail.chapter === 'chapter-1') {
        const newlyCompleted = this.completeTrial('trial-1');
        this.saveState();
        this.syncTrialState();
        if (newlyCompleted) this.syncTrialCompletion('trial-1', 'live');
      }
      if (event.detail && event.detail.chapter === 'chapter-2') {
        const newlyCompleted = this.completeTrial('trial-2');
        if (newlyCompleted) this.didCompleteTrialTwoThisSession = true;
        this.saveState();
        this.syncTrialState();
        if (newlyCompleted) this.syncTrialCompletion('trial-2', 'live');
        this.showView('chapter-select');
        if (this.chapterTwoFinale) this.chapterTwoFinale.hidden = true;
      }
    });

    this.section.addEventListener('axis:trial-two-progress', (event) => {
      const investigatedHotspots = event.detail?.investigatedHotspots;
      if (!Array.isArray(investigatedHotspots)) return;

      this.state.trialTwoInvestigatedHotspots = investigatedHotspots.filter((hotspotId, index, hotspots) => (
        typeof hotspotId === 'string' && hotspots.indexOf(hotspotId) === index
      ));
      this.saveState();
    });

    document.addEventListener('shopify:block:select', (event) => {
      if (!this.section.contains(event.target)) return;
      this.showView('chapter-1', false);
    });
  }

  readState() {
    const defaults = {
      version: 2,
      entered: false,
      currentView: 'entry',
      completedTrials: [],
      trialTwoInvestigatedHotspots: [],
    };
    const validViews = ['entry', 'chapter-select', 'chapter-1', 'chapter-2', 'rewards'];
    const knownTrials = ['trial-1', 'trial-2', 'trial-3', 'trial-4'];

    try {
      const saved = JSON.parse(window.localStorage.getItem(this.storageKey));
      if (saved && typeof saved === 'object') {
        const completedTrials = Array.isArray(saved.completedTrials) ? saved.completedTrials : [];
        if (saved.chapterOneComplete === true) completedTrials.push('trial-1');

        const normalizedView = saved.currentView === 'chapter-1-complete' ? 'chapter-1' : saved.currentView;
        return {
          version: 2,
          entered: Boolean(saved.entered),
          currentView: validViews.includes(normalizedView) ? normalizedView : defaults.currentView,
          completedTrials: completedTrials.filter((trial, index, trials) => (
            knownTrials.includes(trial) && trials.indexOf(trial) === index
          )),
          trialTwoInvestigatedHotspots: Array.isArray(saved.trialTwoInvestigatedHotspots)
            ? saved.trialTwoInvestigatedHotspots.filter((hotspotId, index, hotspots) => (
              typeof hotspotId === 'string' && hotspots.indexOf(hotspotId) === index
            ))
            : [],
        };
      }
    } catch (error) {
      // Ignore corrupt localStorage and use defaults.
    }

    try {
      if (window.localStorage.getItem(this.legacyEntryKey) === 'true') {
        defaults.entered = true;
        defaults.currentView = 'chapter-select';
      }
    } catch (error) {
      // Ignore unavailable localStorage.
    }

    return defaults;
  }

  saveState() {
    try {
      window.localStorage.setItem(this.storageKey, JSON.stringify(this.state));
    } catch (error) {
      // The experience should remain usable even when localStorage is unavailable.
    }
  }

  readChapterOneCompletion() {
    const chapter = this.section.querySelector('[data-axis-chapter]');
    if (chapter && chapter.dataset.complete === 'true') {
      this.completeTrial('trial-1');
      this.saveState();
    }
  }

  hasCompletedTrial(trialId) {
    return this.state.completedTrials.includes(trialId);
  }

  hasUnlockedVaultTrial(trialId) {
    if (trialId === 'trial-2') return this.isServerTrialTwoComplete || this.didCompleteTrialTwoThisSession;
    return this.hasCompletedTrial(trialId);
  }

  completeTrial(trialId) {
    if (this.hasCompletedTrial(trialId)) return false;
    this.state.completedTrials.push(trialId);
    return true;
  }

  getInitialSelectedTrialId() {
    const availableTrials = Array.from(this.trials.values()).filter((trial) => trial.selectable);
    return availableTrials[availableTrials.length - 1]?.id || availableTrials[0]?.id || null;
  }

  selectTrial(trialId, moveFocus = false) {
    const trial = this.trials.get(trialId);
    if (!trial?.selectable || !trial.view || !this.featuredTrial) return;

    this.selectedTrialId = trial.id;
    const isComplete = this.hasCompletedTrial(trial.id);
    this.featuredTrial.dataset.axisTrialCard = trial.id;
    this.featuredTrial.dataset.axisRewardsReference = trial.rewardsReference;
    this.featuredTrial.classList.toggle('is-available', !isComplete);
    this.featuredTrial.classList.toggle('is-complete', isComplete);
    if (trial.artwork) {
      this.featuredTrial.style.setProperty('--axis-trial-background', `url('${trial.artwork.replace(/'/g, '%27')}')`);
    }
    if (this.featuredTrialNumeral) this.featuredTrialNumeral.textContent = trial.numeral;
    if (this.featuredTrialTitle) {
      this.featuredTrialTitle.textContent = trial.title;
      this.featuredTrialTitle.hidden = !trial.title;
    }
    if (this.featuredTrialStatus) this.featuredTrialStatus.textContent = isComplete ? 'Complete' : 'Available';

    this.trialOptions.forEach((option) => {
      const isSelected = option.dataset.axisSelectTrial === trial.id;
      option.hidden = isSelected;
      if (option.dataset.axisTrialSelectable === 'true') {
        option.setAttribute('aria-pressed', String(isSelected));
      }
    });

    this.section.querySelectorAll('[data-axis-open-chapter]').forEach((button) => {
      button.hidden = button.dataset.axisOpenChapter !== trial.view;
    });
    this.section.querySelectorAll('[data-axis-open-prologue]').forEach((button) => {
      button.hidden = trial.id !== 'trial-1';
    });

    if (moveFocus) {
      this.section.querySelector(`[data-axis-open-chapter="${trial.view}"]`)?.focus();
    }
  }

  syncTrialCompletion(trial, source) {
    if (!this.isCustomerAuthenticated || !this.customerEmail || !this.progressEndpoint) return;

    window.fetch(`${this.progressEndpoint}/v1/completions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: this.customerEmail,
        trial,
        source,
      }),
    }).catch(() => {});
  }

  getInitialView() {
    if (!this.canEnterWorld) return 'entry';

    if (!this.state.entered) return 'entry';

    const savedView = this.state.currentView;
    if (savedView === 'chapter-1' || savedView === 'chapter-2') {
      return savedView;
    }

    if (savedView === 'rewards' && this.hasCompletedTrial('trial-1')) return savedView;

    return 'chapter-select';
  }

  openGate() {
    if (!this.modal) {
      this.rememberAuthDestination();
      if (this.accountLoginUrl) window.location.assign(this.accountLoginUrl);
      return;
    }

    this.modal.hidden = false;
    document.body.classList.add('axis-world-gate-open');
  }

  rememberAuthDestination() {
    try {
      window.sessionStorage.setItem(this.authDestinationKey, 'chapter-select');
    } catch (error) {
      // Shopify's return_to parameter remains the primary return mechanism.
    }
  }

  restoreAuthDestination() {
    if (!this.isCustomerAuthenticated) return;

    const url = new URL(window.location.href);
    let destination = url.searchParams.get('axis_destination');

    try {
      destination = destination || window.sessionStorage.getItem(this.authDestinationKey);
      window.sessionStorage.removeItem(this.authDestinationKey);
    } catch (error) {
      // Continue with the return_to marker when sessionStorage is unavailable.
    }

    if (destination === 'chapter-select') {
      this.state.entered = true;
      this.state.currentView = 'chapter-select';
    }

    if (url.searchParams.has('axis_destination')) {
      url.searchParams.delete('axis_destination');
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    }
  }

  openPrologue(trigger, options = {}) {
    if (!this.prologueModal || !this.prologueAudio) return;

    this.prologueTrigger = trigger;
    this.prologueCompletionCallback = options.onComplete || null;
    this.prologueNarration = options.narration || 'trial-1';
    if (options.source && this.prologueSource) {
      this.prologueSource.setAttribute('src', options.source);
      this.prologueSource.setAttribute('type', options.type || 'audio/wav');
      this.prologueAudio.load();
    }
    if (this.prologueCopy) this.prologueCopy.hidden = Boolean(options.hideCopy);
    this.prologueModal.hidden = false;
    document.body.classList.add('axis-world-prologue-open');
    this.prologueAudio.currentTime = 0;
    this.syncPrologueLine();
    this.prologueModal.querySelector('[data-axis-close-prologue]')?.focus();

    const playPromise = this.prologueAudio.play();
    if (playPromise) playPromise.catch(() => {});
  }

  closePrologue() {
    if (!this.prologueModal || !this.prologueAudio) return;

    const onComplete = this.prologueCompletionCallback;
    this.prologueCompletionCallback = null;
    this.prologueAudio.pause();
    this.prologueModal.hidden = true;
    document.body.classList.remove('axis-world-prologue-open');
    this.prologueLines.forEach((line) => line.classList.remove('is-active'));
    this.prologueTrigger?.focus();
    this.prologueTrigger = null;
    this.prologueNarration = 'trial-1';
    if (this.prologueCopy) this.prologueCopy.hidden = false;
    if (this.prologueSource && this.prologueSource.getAttribute('src') !== this.prologueDefaultSource) {
      this.prologueSource.setAttribute('src', this.prologueDefaultSource);
      this.prologueSource.setAttribute('type', this.prologueDefaultType);
      this.prologueAudio.load();
    }
    onComplete?.();
  }

  handlePrologueEnded() {
    this.syncPrologueLine();
    if (this.prologueCompletionCallback) this.closePrologue();
  }

  openTrialTwoNarration(onComplete) {
    if (this.prologueCompletionCallback || !this.prologueAudio?.dataset.axisTrialTwoNarrationUrl) return;

    this.openPrologue(null, {
      source: this.prologueAudio.dataset.axisTrialTwoNarrationUrl,
      type: 'audio/wav',
      narration: 'trial-2',
      onComplete,
    });
  }

  syncPrologueLine() {
    if (!this.prologueAudio || this.prologueLines.length === 0) return;

    const narrationLines = this.prologueLines.filter((line) => (
      this.prologueNarration === 'trial-2'
        ? line.dataset.axisNarration === 'trial-2'
        : line.dataset.axisNarration !== 'trial-2'
    ));
    if (narrationLines.length === 0) return;

    const currentTime = this.prologueAudio.currentTime;
    let activeLine = narrationLines[0];

    narrationLines.forEach((line) => {
      if (currentTime >= Number(line.dataset.start)) activeLine = line;
    });

    this.prologueLines.forEach((line) => {
      line.classList.toggle('is-active', line === activeLine);
    });
  }

  selectVaultVideo(button) {
    const trialElement = button?.closest('[data-axis-vault-collection-trial]');
    const trial = this.vaultTrials.find((entry) => entry.element === trialElement);
    if (!trial?.video || !trial.videoSource || !button) return;

    const videoUrl = button.dataset.videoUrl;
    const posterUrl = button.dataset.videoPoster;
    const label = button.dataset.videoLabel;
    if (!videoUrl || !posterUrl || !label) return;

    const sourceChanged = trial.videoSource.getAttribute('src') !== videoUrl;
    if (sourceChanged) {
      trial.video.pause();
      trial.videoSource.setAttribute('src', videoUrl);
      trial.video.setAttribute('poster', posterUrl);
      trial.video.setAttribute('aria-label', label);
      trial.video.load();
    }

    if (trial.videoTitle) trial.videoTitle.textContent = label;
    trial.videoOptions.forEach((option) => {
      const isActive = option === button;
      option.classList.toggle('is-active', isActive);
      option.setAttribute('aria-pressed', String(isActive));
      option.tabIndex = isActive ? 0 : -1;
    });
  }

  handleVaultVideoKeydown(event, button) {
    const trialElement = button.closest('[data-axis-vault-collection-trial]');
    const trial = this.vaultTrials.find((entry) => entry.element === trialElement);
    if (!trial) return;

    const index = trial.videoOptions.indexOf(button);
    const lastIndex = trial.videoOptions.length - 1;
    let nextIndex = null;

    if (event.key === 'ArrowRight') nextIndex = index === lastIndex ? 0 : index + 1;
    if (event.key === 'ArrowLeft') nextIndex = index === 0 ? lastIndex : index - 1;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = lastIndex;
    if (nextIndex === null) return;

    event.preventDefault();
    const nextButton = trial.videoOptions[nextIndex];
    this.selectVaultVideo(nextButton);
    nextButton.focus();
  }

  getHighestCompletedVaultTrial() {
    return this.vaultTrials
      .filter((trial) => this.hasUnlockedVaultTrial(trial.id))
      .sort((trialA, trialB) => trialB.order - trialA.order)[0] || null;
  }

  selectVaultTrial(trialId, moveFocus = false) {
    const selectedTrial = this.vaultTrials.find((trial) => trial.id === trialId);
    if (!selectedTrial || !this.hasUnlockedVaultTrial(trialId)) return;

    this.vaultTrials.forEach((trial) => {
      const isSelected = trial === selectedTrial;
      trial.element.hidden = !isSelected;
      if (!isSelected) trial.video?.pause();
      if (trial.selector) {
        trial.selector.classList.toggle('is-active', isSelected);
        trial.selector.setAttribute('aria-pressed', String(isSelected));
        trial.selector.tabIndex = isSelected ? 0 : -1;
      }
    });

    if (moveFocus) selectedTrial.selector?.focus();
  }

  handleVaultTrialKeydown(event, currentTrial) {
    const completedTrials = this.vaultTrials
      .filter((trial) => this.hasUnlockedVaultTrial(trial.id))
      .sort((trialA, trialB) => trialA.order - trialB.order);
    const currentIndex = completedTrials.indexOf(currentTrial);
    if (currentIndex === -1) return;

    let nextIndex = null;
    if (event.key === 'ArrowRight') nextIndex = currentIndex === completedTrials.length - 1 ? 0 : currentIndex + 1;
    if (event.key === 'ArrowLeft') nextIndex = currentIndex === 0 ? completedTrials.length - 1 : currentIndex - 1;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = completedTrials.length - 1;
    if (nextIndex === null) return;

    event.preventDefault();
    this.selectVaultTrial(completedTrials[nextIndex].id, true);
  }

  enterWorld() {
    if (!this.canEnterWorld) {
      this.openGate();
      return;
    }

    if (this.introTransitionActive) return;

    this.state.entered = true;
    this.state.currentView = 'chapter-select';
    this.saveState();

    this.playIntroTransition(() => {
      this.showView('chapter-select');
    });
  }

  playIntroTransition(onComplete) {
    const message = 'The tale begins, and so does thy trial';

    if (!this.introTransition || !this.introLine) {
      onComplete();
      return;
    }

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const enterButtons = Array.from(this.section.querySelectorAll('[data-axis-enter-trigger]'));
    this.introTransitionActive = true;
    enterButtons.forEach((button) => {
      button.disabled = true;
    });

    this.introLine.textContent = '';
    this.introTransition.hidden = false;

    if (reduceMotion) {
      this.introLine.textContent = message;
      window.setTimeout(() => {
        this.introTransitionActive = false;
        this.introTransition.hidden = true;
        onComplete();
      }, 900);
      return;
    }

    let index = 0;
    const typeNextCharacter = () => {
      this.introLine.textContent = message.slice(0, index);

      if (index >= message.length) {
        window.setTimeout(() => {
          this.introTransitionActive = false;
          this.introTransition.hidden = true;
          onComplete();
        }, 450);
        return;
      }

      index += 1;
      window.setTimeout(typeNextCharacter, 48);
    };

    typeNextCharacter();
  }

  showView(viewName, save = true) {
    if (viewName !== 'rewards') this.vaultTrials.forEach((trial) => trial.video?.pause());

    this.views.forEach((view) => {
      view.hidden = view.dataset.axisView !== viewName;
    });

    this.section.dataset.axisState = viewName;

    if (viewName === 'rewards') {
      const highestCompletedTrial = this.getHighestCompletedVaultTrial();
      if (highestCompletedTrial) this.selectVaultTrial(highestCompletedTrial.id);
    }

    if (viewName === 'chapter-2') this.broadcastTrialTwoProgress();

    if (save) {
      this.state.currentView = viewName;
      this.saveState();
    }
  }

  syncTrialState() {
    const hasCompletedReward = this.vaultTrials.some((trial) => this.hasUnlockedVaultTrial(trial.id));

    this.trials.forEach((trial) => {
      const trialIsComplete = this.hasCompletedTrial(trial.id);
      trial.option.classList.toggle('is-available', trial.selectable && !trialIsComplete);
      trial.option.classList.toggle('is-complete', trialIsComplete);
      const status = trial.option.querySelector('.axis-world__chapter-status');
      if (status && trial.selectable) status.textContent = trialIsComplete ? 'Complete' : 'Available';
    });
    if (this.vaultDestination) {
      this.vaultDestination.disabled = !hasCompletedReward;
      this.vaultDestination.setAttribute('aria-disabled', String(!hasCompletedReward));
      this.vaultDestination.classList.toggle('is-sealed', !hasCompletedReward);
      this.vaultDestination.classList.toggle('is-unsealed', hasCompletedReward);
    }
    if (this.vaultDestinationLabel) {
      this.vaultDestinationLabel.textContent = hasCompletedReward ? 'Enter the Vault →' : 'Rewards Vault';
    }
    if (this.vaultDestinationStatus) {
      this.vaultDestinationStatus.hidden = hasCompletedReward;
      if (!hasCompletedReward) this.vaultDestinationStatus.textContent = 'Sealed';
    }
    this.vaultTrials.forEach((trial) => {
      const isComplete = this.hasUnlockedVaultTrial(trial.id);
      if (trial.status) trial.status.textContent = isComplete ? 'Complete' : 'Sealed';
      if (trial.locked) trial.locked.hidden = isComplete;
      if (trial.collection) trial.collection.hidden = !isComplete;
      if (trial.selector) {
        trial.selector.hidden = !isComplete;
        trial.selector.disabled = !isComplete;
        trial.selector.setAttribute('aria-disabled', String(!isComplete));
      }
    });
    this.vaultRoadmapItems.forEach((item) => {
      item.hidden = this.hasUnlockedVaultTrial(item.dataset.axisVaultRoadmapTrial);
    });
    if (this.selectedTrialId) this.selectTrial(this.selectedTrialId);
  }

  broadcastTrialTwoProgress() {
    this.section.dispatchEvent(new CustomEvent('axis:trial-two-progress-state', {
      detail: {
        investigatedHotspots: this.state.trialTwoInvestigatedHotspots || [],
      },
    }));
  }
}

function initAxisMundiWorld(container = document) {
  loadAxisMundiTypekit();

  container.querySelectorAll('[data-axis-world]').forEach((section) => {
    if (section.axisMundiWorld) return;
    section.axisMundiWorld = new AxisMundiWorld(section);
  });
}

document.addEventListener('DOMContentLoaded', () => initAxisMundiWorld());
document.addEventListener('shopify:section:load', (event) => initAxisMundiWorld(event.target));
})();
